<template>
  <NavigationLayout
    :navigationBarStyle="{
      'top': 'var(--safe-area-inset-top, 0px)',
    }"
    :navigationContentStyle="{
      'padding-top': 'calc(44px + var(--safe-area-inset-top, 0px))',
    }"
  >
    <div id="mypage__wrapper" slot="contents">
      <div class="complete_img">
        <img src="/img/avatars.webp" />
      </div>
      <div id="mypage__container">
        <p class="purchase_complete">{{ $t('purchaseComplete') }}</p>
        <p class="purchase_thanks">
          {{
            $t('order_details__body__purchase_completed', {
              coin: formatNumber(purchasedCoins),
            })
          }}
          <br />
          {{ $t('purchaseThanks') }}
        </p>
        <div v-if="isUpgradeChargeGrade" class="charge_grade_result">
          <div class="charge_grade_result_icon">
            <img :src="chargeGradeHeaderIconUrl" width="80" />
          </div>
          <div class="charge_grade_result_title">
            <!-- 改行されないように注意 -->
            <p>{{ chargeGradeHeaderText }}</p>
          </div>
          <div class="charge_grade_result_content">
            {{ $t('charge_grade_rank_up__caption') }}
            <div
              v-if="upgradeThresholdSettings.length > 1"
              class="charge_grade_result_grade"
            >
              {{ chargeGradeUpgradeList }}<br />
            </div>
          </div>
          <div class="charge_grade_result_description">
            <ul class="charge_grade_prize_name">
              <li v-for="(prizeName, i) in chargeGradeGrantPrizeList" :key="i">
                {{ prizeName }}
              </li>
            </ul>
          </div>
        </div>
        <PrimaryButton @click.native="moveTo({ name: 'mypage' })">
          {{ $t('order_details__button__back_to_coin_purchase') }}
        </PrimaryButton>
        <PrimaryButton
          v-if="!isUserWebview"
          @click="redirectToCustomURLSchemeForAppOpen"
          color="white"
        >
          {{ $t('openRealityApp') }}
        </PrimaryButton>

        <!-- LINE -->
        <div class="line_wrapper">
          <p class="line_description">
            {{ $t('order_details__body__add_line_friend') }}
          </p>
          <LineButton />
        </div>
      </div>
    </div>
  </NavigationLayout>
</template>

<script lang="ts">
import { Component, Mixins } from 'vue-property-decorator'
import NavigationLayout from '@/components/NavigationLayout.vue'
import PrimaryButton from '@/components/PrimaryButton.vue'
import { LanguageQueryHandler } from '@/mixins/LanguageQuery'
import { RawLocation } from 'vue-router'
import { getTokenFromCookie } from '@/utils/token'
import { redirectToCustomURLSchemeForAppOpen } from '@/utils/adjust_redirector'
import LineButton from '@/components/LineButton.vue'
import { api } from '@/api/appGatewayServerApi'
import Formatter from '@/utils/Formatter'
import * as models from '@/models'
import { checkInMaintenance } from '@/utils/maintenance'
import { handleUnauthorized } from '@/utils/handle_error'

@Component({
  components: {
    NavigationLayout,
    PrimaryButton,
    LineButton,
  },

  // 購入画面からしか遷移できない
  beforeRouteEnter(_to, from, next) {
    const transitions = ['coin_purchase', 'coin_purchase_check']
    if (!from.name || !transitions.includes(from.name)) {
      return next({ name: 'mypage' })
    }
    next()
  },
})
export default class MyPageCoinPurchaseComplete extends Mixins(
  LanguageQueryHandler
) {
  purchaseId: string = ''
  purchasedCoins: string = ''
  upgradeThresholdSettings: models.ChargeGradeThresholdSettings = []
  grantPrizeSettings: models.ChargeGradePrizeSettings = []

  get isUserWebview(): boolean {
    return getTokenFromCookie() !== undefined
  }

  created() {
    this.purchaseId = this.$route.query.purchaseId as string
    this.purchasedCoins = this.$route.query.purchasedCoins as string
    this.fetchChargeGradeCheck()
  }

  async fetchChargeGradeCheck() {
    try {
      // チャージグレードの情報を取得
      const response = await api().charge_grade_web.check_in_web_purchase({
        purchaseId: this.purchaseId,
        purchasedCoin: this.purchasedCoins,
      })

      this.upgradeThresholdSettings = response.upgradeThresholdSettings
      this.grantPrizeSettings = response.grantPrizeSettings
    } catch (error) {
      if (checkInMaintenance(error)) {
        return
      }

      handleUnauthorized(error)
    }
  }

  redirectToCustomURLSchemeForAppOpen = redirectToCustomURLSchemeForAppOpen

  moveTo(location: RawLocation) {
    this.$router.push(location).catch(() => {
      // Nothing to do
    })
  }

  formatNumber(num: string) {
    return Formatter.numberWithCommas(parseInt(num))
  }

  get isUpgradeChargeGrade(): boolean {
    return this.upgradeThresholdSettings.length > 0
  }

  get chargeGradeHeaderText(): string {
    return this.upgradeThresholdSettings[
      this.upgradeThresholdSettings.length - 1
    ].upgradeText
  }

  get chargeGradeHeaderIconUrl(): string {
    return this.upgradeThresholdSettings[
      this.upgradeThresholdSettings.length - 1
    ].statusIconUrl
  }

  get chargeGradeUpgradeList(): string {
    return this.upgradeThresholdSettings
      .map((thresholdSetting) => thresholdSetting.name)
      .join('/')
  }

  get chargeGradeGrantPrizeList(): string[] {
    // コピーを作成して元の配列を変更しないようにする
    const settings = this.grantPrizeSettings.slice()

    // CoinPurchaseBonus タイプの中で最後のものを取得
    const lastCoinPurchaseBonus = settings
      .filter(
        (setting) =>
          setting.prizeType === models.ChargeGradePrizeType.CoinPurchaseBonus
      )
      .pop()

    return (
      settings
        // prizeType でソート
        .sort((a, b) => a.prizeType - b.prizeType)
        // CoinPurchaseBonus タイプのものは、最後の要素だけ残す
        .filter(
          (setting) =>
            setting.prizeType !==
              models.ChargeGradePrizeType.CoinPurchaseBonus ||
            setting === lastCoinPurchaseBonus
        )
        // 表示用のテキストのみを抽出
        .map((prizeSetting) => prizeSetting.prizeText)
    )
  }
}
</script>

<style scoped>
.complete_img {
  width: 100%;
  text-align: center;
  object-fit: contain;
}

.complete_img img {
  width: 100%;
  max-width: 500px;
  height: auto;
}

.purchase_complete {
  margin-top: 31px;
  font-size: 20px;
  line-height: 20px;
  font-weight: bold;
  text-align: center;
}

.purchase_thanks {
  margin-top: 8px;
  font-size: 16px;
  line-height: 20px;
  text-align: center;
  color: #808080;
}

.primary_button {
  margin-top: 24px;
}

.line_wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
  padding: 24px 16px 24px 16px;
  margin-top: 24px;
  margin-bottom: 16px;
  border-radius: 8px;
  border-style: solid;
  border-width: 1px;
  border-color: #e8e8e8;
}

.line_description {
  width: 100%;
  font-size: 12px;
  font-weight: 300;
  line-height: 12px;
  text-align: center;
  color: #0f0f0f;
  margin-bottom: 20px;
  letter-spacing: 0.4px;
}

#mypage__wrapper {
  position: relative;
  height: auto;
  min-height: 100%;
}

#mypage__container {
  font-family: sans-serif;
  font-size: 14px;
  line-height: 1.43;
  color: #262626;
  padding: 0 16px 40px;

  @media (min-width: 800px) {
    width: 800px;
    margin: 0 auto;
  }
}

.charge_grade_result {
  /* Auto layout */
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 24px;
  gap: 8px;
  margin: 32px auto 8px auto;

  max-width: 343px;

  background: #f2f2f2;
  border-radius: 8px;

  /* Inside auto layout */
  flex: none;
  order: 1;
  flex-grow: 0;
}

.charge_grade_result .charge_grade_result_icon {
  /* Frame 10592 */

  width: 80px;
  height: 80px;

  /* Inside auto layout */
  flex: none;
  order: 0;
  flex-grow: 0;
}

.charge_grade_result .charge_grade_result_title {
  width: 100%;
  margin-bottom: 16px;

  font-family: 'Hiragino Sans', sans-serif;
  font-style: normal;
  font-weight: 600;
  font-size: 16px;
  line-height: 24px;

  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  letter-spacing: 0.15px;
  white-space: pre-wrap;

  color: #0f0f0f;

  flex: none;
  order: 0;
  align-self: stretch;
  flex-grow: 0;
}

.charge_grade_result .charge_grade_result_content {
  width: 100%;

  font-family: 'Hiragino Sans', sans-serif;
  font-style: normal;
  font-weight: 600;
  font-size: 12px;
  line-height: 16px;

  text-align: center;
  letter-spacing: 0.4px;

  color: #0f0f0f;

  flex: none;
  order: 0;
  align-self: stretch;
  flex-grow: 0;
}

.charge_grade_result .charge_grade_result_content .charge_grade_result_grade {
  width: 100%;
  margin-top: 4px;

  font-family: 'Hiragino Sans', sans-serif;
  font-style: normal;
  font-weight: 300;
  font-size: 12px;
  line-height: 16px;
  text-align: center;
  letter-spacing: 0.4px;

  color: #7b7b7b;

  flex: none;
  order: 1;
  align-self: stretch;
  flex-grow: 0;
}

.charge_grade_result .charge_grade_result_description {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  padding: 16px;
  margin-bottom: 0px;
  gap: 10px;

  width: 100%;

  background: #ffffff;
  border-radius: 8px;

  /* Inside auto layout */
  flex: none;
  order: 1;
  align-self: stretch;
  flex-grow: 0;
}

.charge_grade_result .charge_grade_result_description .charge_grade_prize_name {
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;

  width: 100%;
  margin-bottom: 0px;

  font-family: 'Hiragino Sans', sans-serif;
  font-style: normal;
  font-weight: 300;
  font-size: 12px;
  line-height: 16px;
  letter-spacing: 0.4px;
  padding-left: 20px;
  text-align: left;

  color: #0f0f0f;

  flex: none;
  order: 0;
  align-self: stretch;
  flex-grow: 0;
}
</style>
